import {
  RoomModel,
  ProgramModel,
  ProjectModel,
  CategoryModel,
  DepartmentModel,
  RoomContentModel,
  ExitPropertyPageMessage,
} from "@app/catalog";

import {
  BreadcrumbChangedMessage,
  BreadcrumbItemToggleMessage,
  IBreadcrumbItemModel,
  Messenger,
} from "@app/shared";
import { CatalogStateService } from "../services";
import {
  SelectCategoryMessage,
  SelectDepartmentMessage,
  SelectProgramMessage,
  SelectProjectMessage,
  SelectRoomMessage,
} from "@app/catalog-common";
import { CommonConstants } from "@app/app-common";
import { SearchModel } from "@app/core";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Router } from "@angular/router";

export class CatalogFilterStateHandler {
  private catalogStateService: CatalogStateService;
  private currentBreadcrumbFilters: IBreadcrumbItemModel[] = [];
  private _searchValues: string[];
  private router: Router;
  
  private applyFilterSubject$ = new Subject<boolean>();

  public initialize(
    catalogStateService: CatalogStateService,
    messenger: Messenger,
    router: Router

  ) {
    this.catalogStateService = catalogStateService;
    this.router = router;

    // Setup debounced filter application
    this.applyFilterSubject$
      .pipe(debounceTime(300)) // 300ms debounce
      .subscribe((val) => {
        this.executeFilter(val);
      });

    messenger.subscribe(
      catalogStateService,
      BreadcrumbChangedMessage,
      (message: BreadcrumbChangedMessage) => {
        if(message.breadcrumbName !== CommonConstants.CATALOG) return;
        this.currentBreadcrumbFilters = message.breadcrumbs;
        this.applyBreadcrumbFilter(false);
      },
    );

    messenger.subscribe(
      catalogStateService,
      SelectProgramMessage,
      (message: SelectProgramMessage) => {
        if (message.selected) {
          messenger.send(new BreadcrumbItemToggleMessage(message.selected, CommonConstants.CATALOG));
        }
      },
    );

    messenger.subscribe(
      catalogStateService,
      SelectProjectMessage,
      (message: SelectProjectMessage) => {
        // if there is already a project breadcrumb remove it
        const projects = this.currentBreadcrumbFilters.filter((b) => b instanceof ProjectModel).map((b) => b as ProjectModel);
        projects.forEach((p) => {
          messenger.send(new BreadcrumbItemToggleMessage(p, CommonConstants.CATALOG));
        })

        // adding a new breadcrumb
        if (message.selected) {
          messenger.send(new BreadcrumbItemToggleMessage(message.selected, CommonConstants.CATALOG));
        }
      },
    );

    messenger.subscribe(
      catalogStateService,
      SelectCategoryMessage,
      (message: SelectCategoryMessage) => {
        if (message.selected) {
          messenger.send(new BreadcrumbItemToggleMessage(message.selected, CommonConstants.CATALOG));
        }
      },
    );

    messenger.subscribe(
      catalogStateService,
      SelectDepartmentMessage,
      (message: SelectDepartmentMessage) => {
        // if there is already a department breadcrumb remove it
        const departments = this.currentBreadcrumbFilters.filter((b) => b instanceof DepartmentModel).map((b) => b as DepartmentModel);
        departments.forEach((d) => {
          messenger.send(new BreadcrumbItemToggleMessage(d, CommonConstants.CATALOG));
        })

        // adding a new breadcrumb
        if (message.selected) {
          messenger.send(new BreadcrumbItemToggleMessage(message.selected, CommonConstants.CATALOG));
        }
      },
    );

    messenger.subscribe(
      catalogStateService,
      SelectRoomMessage,
      (message: SelectRoomMessage) => {
        // if there is already a room breadcrumb remove it
        const rooms = this.currentBreadcrumbFilters.filter((b) => b instanceof RoomModel).map((b) => b as RoomModel);
        rooms.forEach((r) => {
          messenger.send(new BreadcrumbItemToggleMessage(r, CommonConstants.CATALOG));
        })
        
        // adding a new breadcrumb
        if (message.selected) {
          messenger.send(new BreadcrumbItemToggleMessage(message.selected, CommonConstants.CATALOG));
        }
      },
    );


    messenger.subscribe(
      catalogStateService,
      ExitPropertyPageMessage, () => {
        this.applyBreadcrumbFilter(true);
      }
    )
  }

  private applyBreadcrumbFilter(allowReset: boolean = false) {
    // Trigger the debounced filter
    this.applyFilterSubject$.next(allowReset);
  }

  private executeFilter(allowReset: boolean) {
    const roomId = this.currentBreadcrumbFilters
      .filter((b) => b instanceof RoomModel)
      .map((b) => b as RoomModel)
      .map((r) => r.id)[0];
    const departmentId = this.currentBreadcrumbFilters
      .filter((b) => b instanceof DepartmentModel)
      .map((b) => b as DepartmentModel)
      .map((r) => r.id)[0];
    const programId = this.currentBreadcrumbFilters
      .filter((b) => b instanceof ProgramModel)
      .map((b) => b as ProgramModel)
      .map((r) => r.id)[0];
    const projectId = this.currentBreadcrumbFilters
      .filter((b) => b instanceof ProjectModel)
      .map((b) => b as ProjectModel)
      .map((r) => r.id)[0];
    const roomContentId = this.currentBreadcrumbFilters
      .filter((b) => b instanceof RoomContentModel)
      .map((b) => b as RoomContentModel)
      .map((r) => r.id)[0];
    const categoryId = this.currentBreadcrumbFilters
      .filter((b) => b instanceof CategoryModel)
      .map((b) => b as CategoryModel)
      .map((r) => r.id)[0];

    const searchModels = this.currentBreadcrumbFilters
      .filter((val) => val instanceof SearchModel);
    
    if (searchModels.length > 0) {
      this._searchValues = searchModels.map((val) => val.name());
    } else {
      this._searchValues = [];
    }


    const urlParts = this.router.url.split('/').filter(part => part.length > 0);
    const isPropertiesPage = urlParts.length >= 3 && urlParts[0] === CommonConstants.CATALOG;


    if(isPropertiesPage && !allowReset) {
      return;
    }
        
    this.catalogStateService.loadCarousels({
        roomId,
        departmentId,
        programId,
        projectId,
        roomContentId,
        categoryId,
        searchValues: this._searchValues,
      });
  }

  public destroy() {
    this.applyFilterSubject$.complete();
  }
}